<template>
  <Authenticator class="my-2">
    <div class="md my-2">
      <div v-if="badge_administrators !== null">
        <div class="row px-3 py-2 my-0">
          <h1>Badge administrators</h1>
        </div>

        <!-- Add administrator form -->
        <div class="row px-3">
          <form class="mb-3"
                @submit.prevent="add_administrator">
            <input class="form-control w-100 mb-2"
                   data-bs-theme="dark"
                   name="target_email"
                   :placeholder="'...@'+organisation"
                   required />
            <div class="d-flex flex-column my-1">
              <button type="submit"
                      class="btn btn-outline-light align-self-end"
                      data-bs-theme="dark"
                      :disabled="display_not_admin_text || loading">
                Add administrator
              </button>
            </div>
          </form>
        </div>

        <!-- Loading spinner -->
        <div class="d-flex justify-content-center align-items-center"
             v-if="loading">
          <div class="spinner-border m-2 mb-3" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <!-- Alerts -->
        <div class="row mx-3">
          <div v-if="error_message"
               class="alert alert-danger"
               role="alert"
               data-bs-theme="dark">
            {{ error_message }}
          </div>
        </div>
        <div class="row mx-3">
          <div v-if="success_message"
               class="alert alert-success"
               role="alert"
               data-bs-theme="dark">
            {{ success_message }}
          </div>
        </div>

        <!-- Not admin warning -->
        <div class="row mx-3">
          <div v-if="display_not_admin_text"
               class="alert alert-light"
               role="alert"
               data-bs-theme="dark">
            <span>
              You cannot manage badge administrators because you are not a badge administrator for your organisation.
              The badge administrator(s) of your organisation is/are:
            </span>
            <ul class="mb-0">
              <li v-for="admin in sorted_badge_administrators"
                  :key="admin">
                {{admin}}
              </li>
            </ul>
          </div>
        </div>

        <!-- No administrators info -->
        <div class="row mx-3">
          <div v-if="display_no_administrators_text"
               class="alert alert-light"
               role="alert"
               data-bs-theme="dark">
            <span>
              Your organization ({{"...@"+this.organisation}}) has no badge administrators yet.
              Go to <RouterLink to="/badges/award">Award a badge</RouterLink> to become the first badge administrator.
            </span>
          </div>
        </div>

        <!-- Administrators list -->
        <div class="row mx-3" v-if="badge_administrators.length > 0">
          <table class="table table-dark table-hover rounded-table">
            <tbody>
              <tr v-for="admin in sorted_badge_administrators"
                  :key="admin">
                <td class="text-start align-middle">{{ admin }}</td>
                <td class="text-end">
                  <button class="btn btn-outline-danger btn-sm"
                          data-bs-theme="dark"
                          :disabled="display_not_admin_text || loading"
                          @click="remove_administrator(admin)">
                    Remove
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Initial loading spinner -->
      <div class="d-flex justify-content-center align-items-center"
           v-if="badge_administrators === null">
        <div class="spinner-border m-2" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
    </div>
  </Authenticator>
</template>
<script>
  import Authenticator from '@/authenticator/authenticator.js'
  import { get_email, get_badge_administrators, post_badge_administrator, delete_badge_administrator } from '@/backend/backend.js'
  import { get_valid_email } from '@/badges/badges.js'
  export default {
    components: {
      Authenticator
    },
    data() {
      return {
        viewer_email: null,
        error_message: null,
        success_message: null,
        badge_administrators: null,
        loading: false,
      }
    },
    created() {
       document.title = "Badge administrators"
    },
    mounted() {
      get_email().then(
        email => {this.viewer_email = email}
      )

      this.fetch_administrators()
    },
    methods: {
      fetch_administrators() {
        get_badge_administrators()
          .then(badge_administrators => {
            if (!badge_administrators) {
              console.debug('There are no badge administrators')
              this.badge_administrators = []
              return
            }
            this.badge_administrators = badge_administrators
          })
      },
      add_administrator(submit_event) {
        this.error_message = null
        this.success_message = null

        var data = new FormData(submit_event.target)
        var data_object = {}
        data.forEach((value, key) => {
          data_object[key] = value
        })
        var target_email = get_valid_email(data_object.target_email)
        if (!target_email) {
          this.error_message = "Email is not valid."
          return
        }
        var target_organisation = target_email.split('@')[1]
        if (target_organisation != this.organisation) {
          this.error_message = "You can only add administrators from your own organisation (...@" + this.organisation + ")."
          return
        }

        this.loading = true
        post_badge_administrator(target_email)
          .then(api_response => {
            this.loading = false

            if (!api_response) {
              this.error_message = "Something went wrong."
              return
            }

            if (typeof api_response === 'string' && api_response.startsWith('Successfully')) {
              this.success_message = api_response
              this.badge_administrators.push(target_email)
            } else {
              this.error_message = api_response
            }
          })
      },
      remove_administrator(email) {
        this.error_message = null
        this.success_message = null

        this.loading = true
        delete_badge_administrator(email)
          .then(api_response => {
            this.loading = false

            if (!api_response) {
              this.error_message = "Something went wrong."
              return
            }

            if (typeof api_response === 'string' && api_response.startsWith('Successfully')) {
              this.success_message = api_response
              this.badge_administrators = this.badge_administrators.filter(admin => admin !== email)
            } else {
              this.error_message = api_response
            }
          })
      },
    },
    computed: {
      organisation() {
        if (!this.viewer_email) { return null }
        return this.viewer_email.split('@')[1]
      },
      sorted_badge_administrators() {
        if (this.badge_administrators === null) { return [] }
        return [...this.badge_administrators].sort()
      },
      display_not_admin_text() {
        if (this.badge_administrators === null) { return false }
        if (this.badge_administrators.includes(this.viewer_email)) { return false }
        if (this.badge_administrators.length == 0) { return false }
        return true
      },
      display_no_administrators_text() {
        if (this.badge_administrators === null) { return false }
        if (this.badge_administrators.includes(this.viewer_email)) { return false }
        if (this.badge_administrators.length == 0) { return true }
        return false
      },
    }
  }
</script>
<style scoped>
  .rounded-table {
    border-radius: var(--radius-sm);
    overflow: hidden;
    border: 1px solid var(--border-default);
  }
  .rounded-table tr:last-child td {
    border-bottom: none;
  }
  .btn-outline-danger {
    color: var(--danger) !important;
    border-color: var(--danger) !important;
    background: transparent !important;
  }
  .btn-outline-danger:hover {
    background-color: rgba(248, 81, 73, 0.15) !important;
  }
</style>

